// MF-554 — Detail of one Chart Library item: how it looks (sample data, at the size it gets on a
// Board) and everything about it — what the viewer reads, when to use it, where the data comes from,
// who can see it, its options and sizes. Opened from the Library page and the Board Builder panel.
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import BoardTile from '../BoardTile'
import { sampleData } from '../sample'
import { SUMMARY_PALETTE, TILE } from '../boardTheme'
import ItemChips from './ItemChips'
import { AUDIENCE_TEXT, defaultPixelSize } from './libraryUtils'

// the Board canvas background, so the tile and the cards look as they do on a Board
const BOARD_BG = '#f4f7fb'

const TYPE_LABELS = {
  bar: 'Bars',
  hbar: 'Horizontal bars',
  line: 'Line',
  area: 'Area',
  donut: 'Donut',
  kpi: 'KPI',
  table: 'Table'
}

const Field = ({ label, children }) => (
  <Box sx={{ py: 1.25, '& + &': { borderTop: `1px solid ${TILE.border}` } }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: SUMMARY_PALETTE.faint,
        mb: 0.5
      }}
    >
      {label}
    </Typography>
    {typeof children === 'string' ? (
      <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.ink }}>{children}</Typography>
    ) : (
      children
    )}
  </Box>
)

const InfoCard = ({ title, icon, children }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: `1px solid ${TILE.border}`,
      borderRadius: TILE.radius,
      px: 2,
      pt: 1.5,
      pb: 1,
      minWidth: 0
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 700,
        color: SUMMARY_PALETTE.ink,
        display: 'flex',
        gap: 1,
        alignItems: 'center'
      }}
    >
      <FontAwesomeIcon icon={['fal', icon]} style={{ fontSize: 13, color: SUMMARY_PALETTE.faint }} />
      {title}
    </Typography>
    {children}
  </Box>
)

const Lines = ({ items }) =>
  items.map((t) => (
    <Typography key={t} sx={{ fontSize: 13, color: SUMMARY_PALETTE.ink }}>
      {t}
    </Typography>
  ))

/**
 * @param {{ item: Object|null, onClose: Function, onAdd?: Function, onTag?: Function }} props
 *   onAdd: Board Builder — adds the item to the Board. onTag: Library page — filter by that label.
 */
const LibraryItemDialog = ({ item, onClose, onAdd, onTag }) => {
  if (!item) return null
  const size = defaultPixelSize(item)
  const pr = item.presentation
  const options = [
    pr?.types?.length ? `Chart types: ${pr.types.map((t) => TYPE_LABELS[t] || t).join(', ')}` : null,
    pr?.series?.length > 1 ? `Series: ${pr.series.map((s) => s.label).join(', ')}` : null,
    item.card ? `Shows: ${(item.card.fields || []).map((f) => f.label).join(', ')}` : null,
    ...(item.params || []).map((p) => `Can be fixed to one ${String(p.label || p.key).toLowerCase()}`)
  ].filter(Boolean)

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth PaperProps={{ sx: { bgcolor: BOARD_BG } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 3,
          py: 2,
          bgcolor: '#fff',
          borderBottom: `1px solid ${TILE.border}`
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
            {item.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mr: 0.5 }}>{item.group}</Typography>
            <ItemChips item={item} />
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label='Close'>
          <FontAwesomeIcon icon={['fal', 'times']} />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: '20px !important' }}>
        {/* The tile at the size it gets on a Board, on the Board's background. */}
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ width: size.width, maxWidth: '100%', height: size.height }}>
            <BoardTile
              tile={{ id: 'detail', itemCode: item.code, overrides: {} }}
              result={{ loading: false, data: sampleData(item), error: null }}
            />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint, mt: 1, textAlign: 'center' }}>
          Sample data — made-up numbers. On a Board it shows the school’s real data.
        </Typography>

        {/* The details, grouped in white cards (like Board tiles) so they read as separate blocks. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            mt: 2.5,
            alignItems: 'start'
          }}
        >
          <InfoCard title='About' icon='info-circle'>
            <Field label='What viewers read (tile ⓘ)'>{item.description}</Field>
            <Field label='When to use it'>{item.purpose || '—'}</Field>
            <Field label='Labels'>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(item.tags || []).map((t) => (
                  <Chip
                    key={t}
                    size='small'
                    label={t}
                    onClick={onTag ? () => onTag(t) : undefined}
                    sx={{ fontSize: 12 }}
                  />
                ))}
              </Box>
            </Field>
          </InfoCard>
          <InfoCard title='Data and access' icon='database'>
            <Field label='Data from'>
              <Lines items={item.sources?.length ? item.sources : item.providers?.list || []} />
              {!item.compatible ? (
                <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 0.5 }}>{item.reason}</Typography>
              ) : null}
            </Field>
            <Field label='Who can see it'>
              <Lines items={AUDIENCE_TEXT(item.audience, item.sensitive)} />
            </Field>
          </InfoCard>
          <InfoCard title='On a Board' icon='th-large'>
            {options.length ? (
              <Field label='Options'>
                <Lines items={options} />
              </Field>
            ) : null}
            <Field label='Size'>
              {`Starts at ${item.layout?.defaultW} columns × ${item.layout?.defaultH} rows; at least ${item.layout?.minW} × ${item.layout?.minH}`}
            </Field>
            <Field label='Code'>
              <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, fontFamily: 'monospace' }}>
                {item.code} · v{item.version}
              </Typography>
            </Field>
          </InfoCard>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${TILE.border}` }}>
        <Button onClick={onClose}>Close</Button>
        {onAdd ? (
          <Button
            variant='contained'
            disabled={!item.compatible}
            startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
            onClick={() => onAdd(item)}
          >
            Add to board
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

export default LibraryItemDialog
